// Angular imports
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

// 3rd party imports
import { classToPlain, plainToClass } from 'class-transformer';

// RxJs imports
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// Service imports
import { MPCacheRegistrationService } from '../../_services/shared-services/mp-cache-registration-service';

// constant imports
import { URLConstants } from '../../_constants/url-constants';
import { CacheDuration } from '../../_constants/cache-duration';

// Model imports
import { WorkOrderResultDto } from '../_models/work-order-result-dto';
import { BasePropertyParametersDto } from '../_models/base-property-parameter-dto';
import { BaseWorkOrderParameterDto } from '../_models/base-work-order-parameter-dto';
import { BaseProcessNotesResultDto } from '../../management-common/_models/notes/base-process-notes-result-dto';
import { WorkOrderHistoryResultDto } from '../_models/work-order-history-result-dto';
import { WorkOrderStatusChangeParametersDto } from '../_models/work-order-status-change-parameters-dto';
import { AddWorkorderNotesParamaterDto } from '../_models/add-work-order-notes-paramater-dto';
import { FileInfoDisplayResultDto } from '../../_models/file-info-display-result-dto';
import { WorkOrderAttachmentParametersDto } from '../_models/work-order-attachment-parameters-dto';
import { WorkOrderOwnerDocumentsParameterDto } from '../_models/work-order-owner-documents-parameter-dto';
import { DocumentTreeResultDto } from '../../shared-document-module/_models/document-tree-result-dto';
import { WorkOrderVendorListResultDto } from '../_models/work-order-vendor-list-result-dto';
import { BaseWorkOrderProposalParameterDto } from '../_models/base-work-order-proposal-parameter-dto';
import { WorkOrderProposalAcceptParametersDto } from '../_models/work-order-proposal-accept-parameters-dto';
import { WorkOrderProposalRawDto } from '../_models/work-order-proposal-raw-dto';
import { WorkOrderTemporaryVendorsResultDto } from '../_models/work-order-temporary-vendors-result-dto';
import { AddWorkOrderVendorParametersDto } from '../_models/add-work-order-vendor-parameters-dto';
import { WorkOrderManageProposalsResultDto } from '../_models/work-order-manage-proposals-result-dto';
import { VendorProposalHistoryParameterDto } from '../_models/vendor-proposal-history-parameter-dto';
import { VendorProposalHistoryResultDto } from '../_models/vendor-proposal-history-result-dto';
import { ClosedWorkOrderParameterDto } from '../_models/closed-work-order-parameter-dto';
import { WorkOrderManageAttachmentsParametersDto } from '../_models/work-order-manage-attachments-parameters-dto';
import { WorkFlowWorkOrderLinkResultDto } from '../_models/workflow-work-order-link-result-dto';
import { WorkOrderManageWorkFlowParametersDto } from '../_models/work-order-manage-workflow-parameters-dto';
import { WorkOrderManageTaskParametersDto } from '../_models/work-order-manage-task-parameters-dto';
import { ClosedWorkOrderBaseParameterDto } from '../_models/closed-work-order-invoice-parameter-dto';
import { ProjectTaskWorkOrderLinkResultDto } from '../_models/project-task-work-order-link-result-dto';
import { WorkOrderEditorResultDto } from '../_models/work-order-editor-result-dto';
import { WorkOrderResponsibleUsersResultDto } from '../_models/work-order-responsible-users-result-dto';
import { KeyValueResultDto } from '../../_models/key-value-result-dto';
import { WorkOrderReferencesParameterDto } from '../_models/work-order-references-parameter-dto';
import { WorkOrderReferencesResultDto } from '../_models/work-order-references-result-dto';
import { WorkOrderRawDto } from '../_models/work-order-raw-dto';
import { VendorProposalQuoteAttachmentParametersDto } from '../_models/vendor-proposal-quote-attachment-parameters-dto';
import { VendorProposalQuoteAcceptedEditorResultDto } from '../_models/vendor-proposal-quote-accepted-editor-result-dto';
import { WorkOrderProposalAddParametersDto } from '../_models/work-order-proposal-add-parameters-dto';
import { WorkOrderVendorProposalAddDocumentParametersDto } from '../_models/work-order-vendor-proposal-add-document-parameters-dto';
import { WorkOrderProposalQuoteFileParameterDto } from '../_models/work-order-proposal-quote-file-parameter-dto';
import { WorkOrderCreateJobResultDto } from '../_models/work-order-create-job-result-dto';
import { WorkOrderBaseParameterDto } from '../../search/_models/work-order-base-parameter-dto';
import { WorkOrderManageWorkAuthorizationResultDto } from '../_models/work-order-manage-work-authorization-result-dto';
import { WorkOrderWorkAuthorizationFileParameterDto } from '../_models/work-order-work-authorization-file-parameter-dto';
import { WorkAuthorizationVendorParametersDto } from '../_models/work-authorization-vendor-parameters-dto';
import { WorkAuthorizationVendorEstimateParametersDto } from '../_models/work-authorization-vendor-estimate-parameters-dto';
import { AddWorkOrderResultDto } from '../_models/add-work-order-result-dto';
import { WorkAuthorizationAddDocumentParametersDto } from '../_models/work-authorization-add-document-parameters-dto';
import { AddWorkOrderParametersDto } from '../_models/add-work-order-parameters-dto';
import { WorkOrderDocumentResultDto } from '../_models/work-order-document-result-dto';

@Injectable({
    providedIn: 'root'
})
export class WorkOrderService {
    httpHeader = {
        'Content-Type': 'application/json'
    };
    constructor(
        private readonly httpClient: HttpClient,
        private readonly cacheRegistrationService: MPCacheRegistrationService
    ) {}

    /* Workorder API's */

    getOpenWorkOrders(baseParams: BasePropertyParametersDto): Observable<WorkOrderResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/open${baseParams.getQueryString()}`;

        return this.httpClient.get(url).pipe(map((response: any[]) => plainToClass(WorkOrderResultDto, response)));
    }

    getWorkOrderNotes(baseParams: BaseWorkOrderParameterDto): Observable<BaseProcessNotesResultDto> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/notes${baseParams.getQueryString()}`;

        return this.httpClient.get(url).pipe(map((response: any) => plainToClass(BaseProcessNotesResultDto, response)));
    }

    saveNotes(baseParams: AddWorkorderNotesParamaterDto): Observable<any> {
        return this.httpClient.post(`${URLConstants.ccWorkOrderConfigApiUrl}/notes`, classToPlain(baseParams));
    }

    getWorkOrderHistory(baseParams: BaseWorkOrderParameterDto): Observable<WorkOrderHistoryResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/history${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(WorkOrderHistoryResultDto, response)));
    }

    changeStatus(baseParams: WorkOrderStatusChangeParametersDto): Observable<any> {
        return this.httpClient.put(`${URLConstants.ccWorkOrderConfigApiUrl}/status`, classToPlain(baseParams));
    }

    getWorkOrderAttachments(baseParams: BaseWorkOrderParameterDto): Observable<WorkOrderDocumentResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/attachments${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(WorkOrderDocumentResultDto, response)));
    }

    getWorkOrderCommunityDocumentsTree(baseParams: BasePropertyParametersDto): Observable<DocumentTreeResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/community/documents/tree${baseParams.getQueryString()}`;

        return this.httpClient.get(url).pipe(map((response: any[]) => plainToClass(DocumentTreeResultDto, response)));
    }

    getWorkOrderOwnerDocumentsTree(
        baseParams: WorkOrderOwnerDocumentsParameterDto
    ): Observable<DocumentTreeResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/owner/documents/tree${baseParams.getQueryString()}`;

        return this.httpClient.get(url).pipe(map((response: any[]) => plainToClass(DocumentTreeResultDto, response)));
    }

    downloadWorkorderAttachment(baseParams: WorkOrderAttachmentParametersDto): Observable<Blob> {
        const url = `${URLConstants.ccCBARAdjustmentApiUrl}/attachment/download${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    deleteWorkorderAttachments(baseParams: WorkOrderAttachmentParametersDto): Observable<any> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/attachment`;
        const options = {
            headers: new HttpHeaders(this.httpHeader),
            body: classToPlain(baseParams)
        };
        return this.httpClient.delete(url, options);
    }

    uploadWorkorderAttachments(baseParams: WorkOrderManageAttachmentsParametersDto): Observable<any> {
        return this.httpClient.post(`${URLConstants.ccWorkOrderConfigApiUrl}/attachments`, classToPlain(baseParams));
    }

    getClosedWorkOrders(baseParams: ClosedWorkOrderParameterDto): Observable<WorkOrderResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/closed${baseParams.getQueryString()}`;

        return this.httpClient.get(url).pipe(map((response: any[]) => plainToClass(WorkOrderResultDto, response)));
    }

    getWorkOrderEditor(baseParams: BaseWorkOrderParameterDto): Observable<WorkOrderEditorResultDto> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/editor${baseParams.getQueryString()}`;

        return this.httpClient.get(url).pipe(map((response: any) => plainToClass(WorkOrderEditorResultDto, response)));
    }

    getResponsibleUsers(baseParams: BasePropertyParametersDto): Observable<WorkOrderResponsibleUsersResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/responsibleUsers${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(WorkOrderResponsibleUsersResultDto, response)));
    }

    getReferenceTypes(baseParams: BasePropertyParametersDto): Observable<KeyValueResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/referencetypes${baseParams.getQueryString()}`;

        return this.httpClient.get(url).pipe(map((response: any[]) => plainToClass(KeyValueResultDto, response)));
    }

    getReferences(baseParams: WorkOrderReferencesParameterDto): Observable<WorkOrderReferencesResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/references${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(WorkOrderReferencesResultDto, response)));
    }

    updateWorkOrder(baseParams: WorkOrderRawDto): Observable<any> {
        return this.httpClient.put(URLConstants.ccWorkOrderConfigApiUrl, classToPlain(baseParams));
    }

    saveWorkOrder(baseParams: AddWorkOrderParametersDto): Observable<AddWorkOrderResultDto> {
        return this.httpClient
            .post(URLConstants.ccWorkOrderConfigApiUrl, classToPlain(baseParams))
            .pipe(map((response: any) => plainToClass(AddWorkOrderResultDto, response)));
    }

    createMaintenanceJob(baseParams: BaseWorkOrderParameterDto): Observable<WorkOrderCreateJobResultDto> {
        return this.httpClient
            .post(`${URLConstants.ccWorkOrderConfigApiUrl}/maintenancejob`, classToPlain(baseParams))
            .pipe(map((response: any) => plainToClass(WorkOrderCreateJobResultDto, response)));
    }

    /* Manage Workflow API's */

    getOpenInvoices(baseParams: BaseWorkOrderParameterDto): Observable<WorkFlowWorkOrderLinkResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/workflow/available/open${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(WorkFlowWorkOrderLinkResultDto, response)));
    }

    getClosedInvoices(baseParams: ClosedWorkOrderBaseParameterDto): Observable<WorkFlowWorkOrderLinkResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/workflow/available/closed${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(WorkFlowWorkOrderLinkResultDto, response)));
    }

    manageWorkflows(baseParams: WorkOrderManageWorkFlowParametersDto): Observable<any> {
        return this.httpClient.post(
            `${URLConstants.ccWorkOrderConfigApiUrl}/workflows/manage`,
            classToPlain(baseParams)
        );
    }

    /* Manage Tasks */

    getOpenTasks(baseParams: BaseWorkOrderParameterDto): Observable<ProjectTaskWorkOrderLinkResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/tasks/available/open${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(ProjectTaskWorkOrderLinkResultDto, response)));
    }

    getClosedTasks(baseParams: ClosedWorkOrderBaseParameterDto): Observable<ProjectTaskWorkOrderLinkResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/tasks/available/closed${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(ProjectTaskWorkOrderLinkResultDto, response)));
    }

    manageTasks(baseParams: WorkOrderManageTaskParametersDto): Observable<any> {
        return this.httpClient.post(`${URLConstants.ccWorkOrderConfigApiUrl}/tasks/manage`, classToPlain(baseParams));
    }

    /* Workorder Proposal API's */

    getCommunityVendors(baseParams: BasePropertyParametersDto): Observable<WorkOrderVendorListResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/proposal/community/vendors${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(WorkOrderVendorListResultDto, response)));
    }

    getTemporaryVendors(baseParams: BaseWorkOrderParameterDto): Observable<WorkOrderTemporaryVendorsResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/proposal/temporary/vendors${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(WorkOrderTemporaryVendorsResultDto, response)));
    }

    getLocationVendors(baseParams: BasePropertyParametersDto): Observable<WorkOrderVendorListResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/proposal/location/vendors${baseParams.getQueryString()}`;
        this.cacheRegistrationService.registerForCache(url, baseParams.clearCache);
        const headers = this.cacheRegistrationService.getHttpRequestHeaders(url, CacheDuration.fifteenMinute);
        const options = { headers };

        return this.httpClient
            .get(url, options)
            .pipe(map((response: any[]) => plainToClass(WorkOrderVendorListResultDto, response)));
    }

    manageProposals(baseParams: BaseWorkOrderParameterDto): Observable<WorkOrderManageProposalsResultDto> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/manageproposals${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(WorkOrderManageProposalsResultDto, response)));
    }

    deleteWorkOrderProposal(baseParams: BaseWorkOrderProposalParameterDto): Observable<any> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/proposal`;
        const options = {
            headers: new HttpHeaders(this.httpHeader),
            body: classToPlain(baseParams)
        };
        return this.httpClient.delete(url, options);
    }

    acceptWorkOrderProposal(baseParams: WorkOrderProposalAcceptParametersDto): Observable<any> {
        return this.httpClient.put(`${URLConstants.ccWorkOrderConfigApiUrl}/proposal/accept`, classToPlain(baseParams));
    }

    updateWorkOrderProposal(baseParams: WorkOrderProposalRawDto): Observable<any> {
        return this.httpClient.put(`${URLConstants.ccWorkOrderConfigApiUrl}/proposal`, classToPlain(baseParams));
    }

    saveWorkOrderProposal(baseParams: WorkOrderProposalAddParametersDto): Observable<any> {
        return this.httpClient.post(`${URLConstants.ccWorkOrderConfigApiUrl}/proposal`, classToPlain(baseParams));
    }

    regenerateProposalRequest(baseParams: BaseWorkOrderParameterDto): Observable<any> {
        return this.httpClient.post(
            `${URLConstants.ccWorkOrderConfigApiUrl}/proposal/requests/regenerate`,
            classToPlain(baseParams)
        );
    }

    generateProposalRequest(baseParams: BaseWorkOrderProposalParameterDto): Observable<any> {
        return this.httpClient.post(
            `${URLConstants.ccWorkOrderConfigApiUrl}/proposal/request`,
            classToPlain(baseParams)
        );
    }

    addTemporaryVendor(baseParams: AddWorkOrderVendorParametersDto): Observable<any> {
        return this.httpClient.post(
            `${URLConstants.ccWorkOrderConfigApiUrl}/vendor/temporary`,
            classToPlain(baseParams)
        );
    }

    getWorkOrderProposalHistory(
        baseParams: VendorProposalHistoryParameterDto
    ): Observable<VendorProposalHistoryResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/proposal/history${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(VendorProposalHistoryResultDto, response)));
    }

    getProposalQuotes(baseParams: BaseWorkOrderProposalParameterDto): Observable<FileInfoDisplayResultDto[]> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/proposal/quotes${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any[]) => plainToClass(FileInfoDisplayResultDto, response)));
    }

    deleteProposalQuote(baseParams: VendorProposalQuoteAttachmentParametersDto): Observable<any> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/proposal/quote`;
        const options = {
            headers: new HttpHeaders(this.httpHeader),
            body: classToPlain(baseParams)
        };
        return this.httpClient.delete(url, options);
    }

    uploadProposalQuote(baseParams: WorkOrderVendorProposalAddDocumentParametersDto): Observable<any> {
        return this.httpClient.post(`${URLConstants.ccWorkOrderConfigApiUrl}/proposal/quote`, classToPlain(baseParams));
    }

    getQuoteAcceptedEditor(
        baseParams: BaseWorkOrderProposalParameterDto
    ): Observable<VendorProposalQuoteAcceptedEditorResultDto> {
        const url = `${
            URLConstants.ccWorkOrderConfigApiUrl
        }/proposal/quotes/accepted/editor${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(VendorProposalQuoteAcceptedEditorResultDto, response)));
    }

    rfpDownload(baseParam: BaseWorkOrderProposalParameterDto): Observable<Blob> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/proposal/rfp/download${baseParam.getQueryString()}`;
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    /* Manage Work Authorization */

    getWorkAuthorization(baseParams: WorkOrderBaseParameterDto): Observable<WorkOrderManageWorkAuthorizationResultDto> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/workauthorization${baseParams.getQueryString()}`;

        return this.httpClient
            .get(url)
            .pipe(map((response: any) => plainToClass(WorkOrderManageWorkAuthorizationResultDto, response)));
    }

    generateWorkAuthorization(baseParams: BaseWorkOrderParameterDto): Observable<any> {
        return this.httpClient.post(
            `${URLConstants.ccWorkOrderConfigApiUrl}/workauthorization`,
            classToPlain(baseParams)
        );
    }

    downloadWorkAuthorizationDocument(baseParams: WorkOrderWorkAuthorizationFileParameterDto): Observable<Blob> {
        const url = `${URLConstants.ccWorkOrderConfigApiUrl}/workauthorization/download${baseParams.getQueryString()}`;
        return this.httpClient
            .get(url, { responseType: 'arraybuffer' })
            .pipe(map((responseData: any) => new Blob([responseData])));
    }

    updateWorkAuthorizationVendor(baseParams: WorkAuthorizationVendorParametersDto): Observable<any> {
        return this.httpClient.put(
            `${URLConstants.ccWorkOrderConfigApiUrl}/workauthorization/vendor`,
            classToPlain(baseParams)
        );
    }

    updateWorkAuthorizationVendorEstimate(baseParams: WorkAuthorizationVendorEstimateParametersDto): Observable<any> {
        return this.httpClient.put(
            `${URLConstants.ccWorkOrderConfigApiUrl}/workauthorization/vendor/estimate`,
            classToPlain(baseParams)
        );
    }

    uploadWorkAuthorizationDocument(baseParams: WorkAuthorizationAddDocumentParametersDto): Observable<any> {
        return this.httpClient.post(
            `${URLConstants.ccWorkOrderConfigApiUrl}/workauthorization/document`,
            classToPlain(baseParams)
        );
    }
}
